import { useCallback, useContext } from "react";
//types
import {
    MARKETPLACE_BUSINESS_LINE,
    MARKETPLACE_CATEGORY,
    MARKETPLACE_ID,
    GiftingLocalCart,
    IMShipmentDetails,
    CartItem,
    IMStructureItem,
} from "@swiggy-private/cart-types";
import { GiftingCartState } from "@cart-client/reducers/GiftingCartReducer";
//helpers
import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { removeExcessItemsFromGiftingCart } from "@cart-client/helpers/GiftingCart";
import { doesItemBelongToShipment, isIMShipmentServiceable } from "@cart-client/helpers/GiftingIMSubCart";
import { isEmpty } from "@swiggy-private/js-utils";
//contexts
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";
//hooks
import { useGetGiftingCartItemsRenderingProps } from "@cart-client/hooks/useGetGiftingCartItemsRenderingProps";
import { useGetGiftingStoreOOSCart } from "@cart-client/hooks/useGiftingStoreOOSCart";
import { useGiftCartItemActions } from "@cart-client/hooks/useGiftCartItemActions";
import { isItemAvailable } from "@swiggy-private/cart-ui";

export const useGiftingCartFooterActions = () => {
    const { foodCartItemList, imCartItemList } = useGetGiftingCartItemsRenderingProps();
    const { onRemoveOOSItemsListCallback, oosItemCount } = useGetGiftingStoreOOSCart({
        foodCartItemList,
        imCartItemList,
    });

    const { onRemoveOOSItems } = useGiftCartItemActions({
        foodCartItemList: foodCartItemList || [],
        imCartItemList: imCartItemList || [],
    });

    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse, localCart } = cartState as GiftingCartState;

    const clearGiftingCart = useCallback(async () => {
        getBridgeSdk().clearGiftingLocalCart({
            tenant: {
                marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.GIFTING,
                marketplaceCategory: MARKETPLACE_CATEGORY.GIFTING,
                marketplaceId: MARKETPLACE_ID.SWIGGY,
            },
        });
        await doUpdateCart({
            ...localCart,
            foodCartItems: [],
            imCartItems: [],
        } as GiftingLocalCart);
    }, [doUpdateCart, localCart]);

    const handleRemoveOOSItems = useCallback(async () => {
        const remainingFoodItems = foodCartItemList?.filter(item => isItemAvailable(item));
        const remainingImItems = imCartItemList?.filter(item => item.outOfStock !== true);

        if (isEmpty(remainingFoodItems) && isEmpty(remainingImItems)) {
            await clearGiftingCart();
        } else {
            onRemoveOOSItemsListCallback();
        }
    }, [foodCartItemList, imCartItemList, clearGiftingCart, onRemoveOOSItemsListCallback]);

    const handleRemoveUnserviceableItems = useCallback(async () => {
        if (updateCartInProgress) return;

        const { foodCartItems, imShipmentDetails, imCartItems } = cartResponse?.data || {};

        let foodItemsToRemove: CartItem[] = [];
        let imItemsToRemove: IMStructureItem[] = [];

        if (!isEmpty(foodCartItems) && !foodCartItems.isServiceable) {
            foodItemsToRemove = foodCartItems.items || [];
        }

        if (!isEmpty(imShipmentDetails) && !isEmpty(imCartItems)) {
            const unserviceableIMItems =
                imCartItems?.filter(imItem => {
                    const itemShipment = imShipmentDetails.find((shipment: IMShipmentDetails) =>
                        doesItemBelongToShipment(imItem, shipment),
                    );

                    return itemShipment && !isIMShipmentServiceable(itemShipment);
                }) || [];

            imItemsToRemove = unserviceableIMItems;
        }

        // If no unserviceable items found, return early
        if (isEmpty(foodItemsToRemove) && isEmpty(imItemsToRemove)) {
            return;
        }

        const hasServiceableFoodItems = foodCartItems?.isServiceable && !isEmpty(foodCartItems);
        const hasServiceableIMItems = imShipmentDetails?.some((shipment: IMShipmentDetails) => {
            const hasItemsInShipment = imCartItems?.some(item => doesItemBelongToShipment(item, shipment));
            return isIMShipmentServiceable(shipment) && hasItemsInShipment;
        });

        if (!hasServiceableFoodItems && !hasServiceableIMItems) {
            await clearGiftingCart();
        } else {
            await onRemoveOOSItems({
                foodItemsToRemove: !isEmpty(foodItemsToRemove) ? foodItemsToRemove : undefined,
                imItemsToRemove: !isEmpty(imItemsToRemove) ? imItemsToRemove : undefined,
            });
        }
    }, [updateCartInProgress, cartResponse, clearGiftingCart, onRemoveOOSItems]);

    const handleItemLimitExceeded = useCallback(
        async (itemLimitExceededList?: Array<{ title: string; quantity: number; imageId: string }>) => {
            if (isEmpty(itemLimitExceededList) || updateCartInProgress || !localCart) return;

            const updatedLocalCart = removeExcessItemsFromGiftingCart(localCart, cartResponse, itemLimitExceededList);

            await doUpdateCart(updatedLocalCart);
        },
        [updateCartInProgress, localCart, cartResponse, doUpdateCart],
    );

    return {
        clearGiftingCart,
        handleRemoveOOSItems,
        handleRemoveUnserviceableItems,
        handleItemLimitExceeded,
        oosItemCount,
    };
};
